import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatPrice, STATUS_LABELS, STATUS_COLORS } from "@/lib/api";

export default function InventoryList() {
  const [boats, setBoats] = useState([]);
  const [condition, setCondition] = useState("all");
  const [status, setStatus] = useState("all");

  const load = useCallback(() => {
    const qp = new URLSearchParams();
    if (condition !== "all") qp.set("condition", condition);
    if (status !== "all") qp.set("status", status);
    api.get(`/admin/inventory?${qp.toString()}`).then((r) => setBoats(r.data || [])).catch(() => {});
  }, [condition, status]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Permanently delete this boat? This cannot be undone.")) return;
    try { await api.delete(`/admin/inventory/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-inventory-list">
      <AdminHeader title="Inventory" subtitle={`${boats.length} boats`}
        action={<Button asChild className="bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full"><Link to="/admin/inventory/new" data-testid="add-boat-button"><Plus size={16} /> Add Boat</Link></Button>} />

      <div className="flex gap-3 mb-5">
        <Select value={condition} onValueChange={setCondition}><SelectTrigger className="w-40" data-testid="inv-filter-condition"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Conditions</SelectItem><SelectItem value="new">New</SelectItem><SelectItem value="used">Used</SelectItem></SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44" data-testid="inv-filter-status"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Statuses</SelectItem>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono"><tr>
            <th className="text-left px-4 py-3">Boat</th><th className="text-left px-4 py-3 hidden md:table-cell">Stock</th>
            <th className="text-left px-4 py-3 hidden md:table-cell">Price</th><th className="text-left px-4 py-3">Status</th>
            <th className="text-left px-4 py-3 hidden lg:table-cell">Cond.</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {boats.map((b) => (
              <tr key={b.id} className="hover:bg-slate-50" data-testid={`inv-row-${b.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/admin/inventory/${b.id}`} className="font-semibold text-slate-900 hover:text-mtm-purple flex items-center gap-1.5">
                    {b.featured && <Star size={13} className="text-amber-400 fill-amber-400" />}{b.year} {b.brand} {b.model}
                  </Link>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500 hidden md:table-cell">{b.stock_number || "N/A"}</td>
                <td className="px-4 py-3 hidden md:table-cell">{b.price ? formatPrice(b.promo_price || b.price) : ", "}</td>
                <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_COLORS[b.status]}`}>{STATUS_LABELS[b.status]}</span></td>
                <td className="px-4 py-3 hidden lg:table-cell capitalize text-slate-600">{b.condition}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button asChild variant="ghost" size="icon" className="text-slate-400 hover:text-mtm-purple"><Link to={`/admin/inventory/${b.id}`} data-testid={`inv-edit-${b.id}`}><Pencil size={15} /></Link></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(b.id)} className="text-slate-400 hover:text-red-600" data-testid={`inv-delete-${b.id}`}><Trash2 size={15} /></Button>
                </td>
              </tr>
            ))}
            {!boats.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No boats match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
